import { conflicts } from '../data/conflicts';
import { categoryColor, categoryLabel } from '../utils';

interface Node {
  id: string;
  label: string;
  type: 'conflict' | 'actor';
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  connections: number;
}

interface Link {
  source: string;
  target: string;
}

export function renderNetworkView(container: HTMLElement): void {
  const wrapper = document.createElement('div');
  wrapper.className = 'view-container';
  container.appendChild(wrapper);

  // Build nodes and links
  const nodes: Node[] = [];
  const links: Link[] = [];
  const actorConflicts = new Map<string, string[]>(); // actor → conflict IDs

  // Add conflict nodes
  for (const c of conflicts) {
    nodes.push({
      id: c.id,
      label: c.name,
      type: 'conflict',
      x: 400 + Math.random() * 400,
      y: 200 + Math.random() * 300,
      vx: 0, vy: 0,
      radius: Math.max(6, Math.min(18, Math.sqrt(c.deaths2025 / 500))),
      color: categoryColor(c.category),
      connections: 0,
    });

    // Collect actors
    const allActors = [...c.sideA, ...c.sideB];
    for (const actor of allActors) {
      const existing = actorConflicts.get(actor);
      if (existing) {
        existing.push(c.id);
      } else {
        actorConflicts.set(actor, [c.id]);
      }
    }
  }

  // Only include actors involved in 2+ conflicts (otherwise too cluttered)
  const multiConflictActors = [...actorConflicts.entries()].filter(([, ids]) => ids.length >= 2);

  for (const [actor, conflictIds] of multiConflictActors) {
    const nodeId = `actor-${actor}`;
    nodes.push({
      id: nodeId,
      label: actor,
      type: 'actor',
      x: 400 + Math.random() * 400,
      y: 200 + Math.random() * 300,
      vx: 0, vy: 0,
      radius: 5 + conflictIds.length * 2,
      color: '#f59e0b',
      connections: conflictIds.length,
    });
    for (const cid of conflictIds) {
      links.push({ source: nodeId, target: cid });
    }
  }

  // Update connection counts
  for (const link of links) {
    const sNode = nodes.find(n => n.id === link.source);
    const tNode = nodes.find(n => n.id === link.target);
    if (sNode) sNode.connections++;
    if (tNode) tNode.connections++;
  }

  // Simple force simulation
  const W = 1200, H = 700;
  const iterations = 80;

  for (let iter = 0; iter < iterations; iter++) {
    const alpha = 1 - iter / iterations;

    // Repulsion between all nodes
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i], b = nodes[j];
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const minDist = a.radius + b.radius + 30;
        if (dist < minDist) {
          const force = (minDist - dist) * 0.5 * alpha;
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          a.vx -= fx; a.vy -= fy;
          b.vx += fx; b.vy += fy;
        }
        // General repulsion
        const repForce = 800 * alpha / (dist * dist);
        const rfx = (dx / dist) * repForce;
        const rfy = (dy / dist) * repForce;
        a.vx -= rfx; a.vy -= rfy;
        b.vx += rfx; b.vy += rfy;
      }
    }

    // Attraction along links
    for (const link of links) {
      const a = nodes.find(n => n.id === link.source)!;
      const b = nodes.find(n => n.id === link.target)!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const targetDist = 80;
      const force = (dist - targetDist) * 0.01 * alpha;
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      a.vx += fx; a.vy += fy;
      b.vx -= fx; b.vy -= fy;
    }

    // Center gravity
    for (const n of nodes) {
      n.vx += (W / 2 - n.x) * 0.002 * alpha;
      n.vy += (H / 2 - n.y) * 0.002 * alpha;
    }

    // Apply velocities with damping
    for (const n of nodes) {
      n.vx *= 0.7;
      n.vy *= 0.7;
      n.x += n.vx;
      n.y += n.vy;
      // Bounds
      n.x = Math.max(n.radius + 10, Math.min(W - n.radius - 10, n.x));
      n.y = Math.max(n.radius + 10, Math.min(H - n.radius - 10, n.y));
    }
  }

  // Render
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  wrapper.innerHTML = `
    <div class="view-header">
      <h2>Conflict–Actor Network</h2>
      <p>Actors (organisations, militaries, armed groups) that operate across multiple conflicts. Only actors involved in 2+ conflicts are shown. Node size = fatalities. Hover for details.</p>
    </div>

    <div class="network-container">
      <svg width="100%" height="100%" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet">
        <!-- Links -->
        ${links.map(l => {
          const s = nodeMap.get(l.source)!;
          const t = nodeMap.get(l.target)!;
          return `<line class="network-link" x1="${s.x}" y1="${s.y}" x2="${t.x}" y2="${t.y}"/>`;
        }).join('')}

        <!-- Nodes -->
        ${nodes.map(n => `
          <g class="network-node" transform="translate(${n.x},${n.y})">
            <circle r="${n.radius}" fill="${n.color}" opacity="${n.type === 'conflict' ? 0.7 : 0.9}" stroke="${n.type === 'actor' ? '#f59e0b' : 'none'}" stroke-width="${n.type === 'actor' ? 1.5 : 0}">
              <title>${n.label}${n.type === 'actor' ? ` (${n.connections} conflicts)` : ''}</title>
            </circle>
            ${n.radius > 8 ? `<text class="node-label" text-anchor="middle" dy="${n.radius + 14}" style="font-size:${n.type === 'actor' ? '9' : '10'}px">${truncate(n.label, 25)}</text>` : ''}
          </g>
        `).join('')}
      </svg>
    </div>

    <div style="display:flex;gap:20px;margin-top:var(--space-lg);font-size:var(--font-size-sm);color:var(--text-secondary);flex-wrap:wrap;">
      <span><span style="color:${categoryColor('major_war')}">●</span> ${categoryLabel('major_war')}</span>
      <span><span style="color:${categoryColor('minor_war')}">●</span> ${categoryLabel('minor_war')}</span>
      <span><span style="color:${categoryColor('conflict')}">●</span> ${categoryLabel('conflict')}</span>
      <span><span style="color:${categoryColor('skirmish')}">●</span> ${categoryLabel('skirmish')}</span>
      <span><span style="color:#f59e0b">●</span> Multi-conflict actor</span>
    </div>

    <div class="chart-panel" style="margin-top:var(--space-2xl);">
      <h3>Cross-Conflict Actors (${multiConflictActors.length})</h3>
      <p class="chart-subtitle">Organisations and forces involved in 2 or more distinct conflicts</p>
      <table class="data-table" style="font-size:var(--font-size-sm);">
        <thead><tr><th>Actor</th><th>Conflicts</th><th>Involved In</th></tr></thead>
        <tbody>
          ${multiConflictActors
            .sort((a, b) => b[1].length - a[1].length)
            .map(([actor, ids]) => `
              <tr>
                <td style="font-weight:600">${actor}</td>
                <td class="mono">${ids.length}</td>
                <td style="font-size:var(--font-size-xs);color:var(--text-secondary)">${ids.map(id => conflicts.find(c => c.id === id)?.name || id).join(', ')}</td>
              </tr>
            `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}
